// ---------------------------------------------------------------------------
// Audit Log - mock entries for demonstration
//
// Used as a fallback when the API returns an empty list so the UI always has
// something to render during local development. Swap to live API data by
// ignoring the `usingMock` path on the calling page.
// ---------------------------------------------------------------------------

import type { AuditLogEntry } from "@/lib/admin-api/audit-log";

export function generateMockEntries(): readonly AuditLogEntry[] {
  const now = new Date();
  const users = [
    { id: "u1", full_name: "Aminata Diallo", email: "aminata@djayma.com" },
    { id: "u2", full_name: "Moussa Ndiaye", email: "moussa@djayma.com" },
    { id: "u3", full_name: "Fatou Sow", email: "fatou@djayma.com" },
    { id: "u4", full_name: "Ibrahima Ba", email: "ibrahima@djayma.com" },
    { id: "u5", full_name: "Aissatou Diop", email: "aissatou@djayma.com" },
  ];

  const entries: AuditLogEntry[] = [
    {
      id: "al-001", admin_user_id: "u1", resource: "orders", action: "create",
      resource_id: "CMD-2026-0487", before_data: null,
      after_data: { status: "pending", total: 125000, client: "Boutique Mariste" },
      metadata: { ip: "192.168.1.45", department: "Commerce" },
      created_at: new Date(now.getTime() - 5 * 60_000).toISOString(),
      admin_user: users[0],
    },
    {
      id: "al-002", admin_user_id: "u2", resource: "products", action: "update",
      resource_id: "PRD-0089", before_data: { price: 3500, name: "Lait Countre 1L" },
      after_data: { price: 3750, name: "Lait Countre Dairy 1L" },
      metadata: { ip: "192.168.1.32", department: "Produits" },
      created_at: new Date(now.getTime() - 12 * 60_000).toISOString(),
      admin_user: users[1],
    },
    {
      id: "al-003", admin_user_id: "u3", resource: "customers", action: "view",
      resource_id: "CLT-0234", before_data: null, after_data: null,
      metadata: { ip: "192.168.1.78", department: "CRM" },
      created_at: new Date(now.getTime() - 18 * 60_000).toISOString(),
      admin_user: users[2],
    },
    {
      id: "al-004", admin_user_id: "u1", resource: "payments", action: "status_change",
      resource_id: "PAY-2026-1102", before_data: { status: "pending" },
      after_data: { status: "confirmed", method: "Wave" },
      metadata: { ip: "192.168.1.45", department: "Finance" },
      created_at: new Date(now.getTime() - 25 * 60_000).toISOString(),
      admin_user: users[0],
    },
    {
      id: "al-005", admin_user_id: "u4", resource: "settings", action: "update",
      resource_id: null, before_data: { maintenance_mode: false },
      after_data: { maintenance_mode: false, sms_notifications: true },
      metadata: { ip: "192.168.1.12", department: "Administration" },
      created_at: new Date(now.getTime() - 35 * 60_000).toISOString(),
      admin_user: users[3],
    },
    {
      id: "al-006", admin_user_id: "u5", resource: "orders", action: "export",
      resource_id: null, before_data: null,
      after_data: { format: "csv", count: 245, period: "Mars 2026" },
      metadata: { ip: "192.168.1.55", department: "Commerce" },
      created_at: new Date(now.getTime() - 45 * 60_000).toISOString(),
      admin_user: users[4],
    },
    {
      id: "al-007", admin_user_id: "u2", resource: "admin_users", action: "create",
      resource_id: "ADM-0014", before_data: null,
      after_data: { full_name: "Ousmane Fall", role: "commercial", email: "ousmane@djayma.com" },
      metadata: { ip: "192.168.1.32", department: "Administration" },
      created_at: new Date(now.getTime() - 1 * 3600_000).toISOString(),
      admin_user: users[1],
    },
    {
      id: "al-008", admin_user_id: "u3", resource: "products", action: "delete",
      resource_id: "PRD-0045", before_data: { name: "Mayonnaise Yousra 250g (ancien format)", price: 1200 },
      after_data: null,
      metadata: { ip: "192.168.1.78", department: "Produits" },
      created_at: new Date(now.getTime() - 1.5 * 3600_000).toISOString(),
      admin_user: users[2],
    },
    {
      id: "al-009", admin_user_id: "u1", resource: "deliveries", action: "status_change",
      resource_id: "LIV-2026-0389", before_data: { status: "en_cours" },
      after_data: { status: "livree", delivered_at: "2026-03-30T14:22:00Z" },
      metadata: { ip: "192.168.1.45", department: "Logistique" },
      created_at: new Date(now.getTime() - 2 * 3600_000).toISOString(),
      admin_user: users[0],
    },
    {
      id: "al-010", admin_user_id: "u4", resource: "customers", action: "update",
      resource_id: "CLT-0156", before_data: { segment: "standard" },
      after_data: { segment: "premium", credit_limit: 500000 },
      metadata: { ip: "192.168.1.12", department: "CRM" },
      created_at: new Date(now.getTime() - 2.5 * 3600_000).toISOString(),
      admin_user: users[3],
    },
    {
      id: "al-011", admin_user_id: "u5", resource: "orders", action: "update",
      resource_id: "CMD-2026-0480", before_data: { status: "confirmed" },
      after_data: { status: "shipped", tracking: "YG-2026-8834" },
      metadata: { ip: "192.168.1.55", department: "Commerce" },
      created_at: new Date(now.getTime() - 3 * 3600_000).toISOString(),
      admin_user: users[4],
    },
    {
      id: "al-012", admin_user_id: "u2", resource: "products", action: "create",
      resource_id: "PRD-0102", before_data: null,
      after_data: { name: "Riz Yousra 5kg Premium", price: 4500, category: "Riz" },
      metadata: { ip: "192.168.1.32", department: "Produits" },
      created_at: new Date(now.getTime() - 3.5 * 3600_000).toISOString(),
      admin_user: users[1],
    },
    {
      id: "al-013", admin_user_id: "u3", resource: "payments", action: "create",
      resource_id: "PAY-2026-1098", before_data: null,
      after_data: { amount: 87500, method: "Orange Money", order: "CMD-2026-0478" },
      metadata: { ip: "192.168.1.78", department: "Finance" },
      created_at: new Date(now.getTime() - 4 * 3600_000).toISOString(),
      admin_user: users[2],
    },
    {
      id: "al-014", admin_user_id: "u1", resource: "admin_users", action: "login",
      resource_id: "u1", before_data: null, after_data: null,
      metadata: { ip: "192.168.1.45", user_agent: "Chrome/125", department: "Commerce" },
      created_at: new Date(now.getTime() - 4.5 * 3600_000).toISOString(),
      admin_user: users[0],
    },
    {
      id: "al-015", admin_user_id: "u4", resource: "orders", action: "delete",
      resource_id: "CMD-2026-0412", before_data: { status: "cancelled", total: 0 },
      after_data: null,
      metadata: { ip: "192.168.1.12", department: "Commerce", reason: "Commande test supprimee" },
      created_at: new Date(now.getTime() - 5 * 3600_000).toISOString(),
      admin_user: users[3],
    },
    {
      id: "al-016", admin_user_id: "u5", resource: "deliveries", action: "create",
      resource_id: "LIV-2026-0390", before_data: null,
      after_data: { order: "CMD-2026-0485", driver: "Aliou Cisse", zone: "Dakar" },
      metadata: { ip: "192.168.1.55", department: "Logistique" },
      created_at: new Date(now.getTime() - 5.5 * 3600_000).toISOString(),
      admin_user: users[4],
    },
    {
      id: "al-017", admin_user_id: "u2", resource: "customers", action: "create",
      resource_id: "CLT-0301", before_data: null,
      after_data: { name: "Superette Touba Express", phone: "+221 77 123 45 67", region: "Diourbel-Touba" },
      metadata: { ip: "192.168.1.32", department: "CRM" },
      created_at: new Date(now.getTime() - 6 * 3600_000).toISOString(),
      admin_user: users[1],
    },
    {
      id: "al-018", admin_user_id: "u3", resource: "settings", action: "update",
      resource_id: null, before_data: { delivery_fee: 1500 },
      after_data: { delivery_fee: 2000 },
      metadata: { ip: "192.168.1.78", department: "Administration" },
      created_at: new Date(now.getTime() - 7 * 3600_000).toISOString(),
      admin_user: users[2],
    },
    {
      id: "al-019", admin_user_id: "u4", resource: "products", action: "update",
      resource_id: "PRD-0067", before_data: { stock: 250, status: "active" },
      after_data: { stock: 0, status: "out_of_stock" },
      metadata: { ip: "192.168.1.12", department: "Produits" },
      created_at: new Date(now.getTime() - 8 * 3600_000).toISOString(),
      admin_user: users[3],
    },
    {
      id: "al-020", admin_user_id: "u1", resource: "orders", action: "view",
      resource_id: "CMD-2026-0475", before_data: null, after_data: null,
      metadata: { ip: "192.168.1.45", department: "Commerce" },
      created_at: new Date(now.getTime() - 9 * 3600_000).toISOString(),
      admin_user: users[0],
    },
    {
      id: "al-021", admin_user_id: "u5", resource: "payments", action: "status_change",
      resource_id: "PAY-2026-1090", before_data: { status: "failed" },
      after_data: { status: "refunded", refund_amount: 45000 },
      metadata: { ip: "192.168.1.55", department: "Finance" },
      created_at: new Date(now.getTime() - 10 * 3600_000).toISOString(),
      admin_user: users[4],
    },
    {
      id: "al-022", admin_user_id: "u2", resource: "admin_users", action: "update",
      resource_id: "ADM-0008", before_data: { role: "viewer" },
      after_data: { role: "editor", permissions: ["orders", "products"] },
      metadata: { ip: "192.168.1.32", department: "Administration" },
      created_at: new Date(now.getTime() - 12 * 3600_000).toISOString(),
      admin_user: users[1],
    },
    {
      id: "al-023", admin_user_id: "u3", resource: "deliveries", action: "status_change",
      resource_id: "LIV-2026-0385", before_data: { status: "assigned" },
      after_data: { status: "en_cours", started_at: "2026-03-29T08:15:00Z" },
      metadata: { ip: "192.168.1.78", department: "Logistique" },
      created_at: new Date(now.getTime() - 14 * 3600_000).toISOString(),
      admin_user: users[2],
    },
    {
      id: "al-024", admin_user_id: "u4", resource: "customers", action: "delete",
      resource_id: "CLT-0089", before_data: { name: "Test Client (doublon)", status: "inactive" },
      after_data: null,
      metadata: { ip: "192.168.1.12", department: "CRM", reason: "Doublon supprime" },
      created_at: new Date(now.getTime() - 16 * 3600_000).toISOString(),
      admin_user: users[3],
    },
    {
      id: "al-025", admin_user_id: "u5", resource: "products", action: "export",
      resource_id: null, before_data: null,
      after_data: { format: "csv", count: 89, category: "Tous les produits" },
      metadata: { ip: "192.168.1.55", department: "Produits" },
      created_at: new Date(now.getTime() - 18 * 3600_000).toISOString(),
      admin_user: users[4],
    },
    {
      id: "al-026", admin_user_id: "u1", resource: "orders", action: "status_change",
      resource_id: "CMD-2026-0470", before_data: { status: "processing" },
      after_data: { status: "delivered" },
      metadata: { ip: "192.168.1.45", department: "Commerce" },
      created_at: new Date(now.getTime() - 20 * 3600_000).toISOString(),
      admin_user: users[0],
    },
    {
      id: "al-027", admin_user_id: "u2", resource: "payments", action: "view",
      resource_id: "PAY-2026-1085", before_data: null, after_data: null,
      metadata: { ip: "192.168.1.32", department: "Finance" },
      created_at: new Date(now.getTime() - 22 * 3600_000).toISOString(),
      admin_user: users[1],
    },
    {
      id: "al-028", admin_user_id: "u3", resource: "admin_users", action: "login",
      resource_id: "u3", before_data: null, after_data: null,
      metadata: { ip: "192.168.1.78", user_agent: "Firefox/126", department: "CRM" },
      created_at: new Date(now.getTime() - 24 * 3600_000).toISOString(),
      admin_user: users[2],
    },
    {
      id: "al-029", admin_user_id: "u4", resource: "orders", action: "create",
      resource_id: "CMD-2026-0468", before_data: null,
      after_data: { status: "pending", total: 310000, client: "Depot Kaolack Centre" },
      metadata: { ip: "192.168.1.12", department: "Commerce" },
      created_at: new Date(now.getTime() - 26 * 3600_000).toISOString(),
      admin_user: users[3],
    },
    {
      id: "al-030", admin_user_id: "u5", resource: "settings", action: "update",
      resource_id: null, before_data: { currency_display: "FCFA" },
      after_data: { currency_display: "FCFA", tax_rate: 18 },
      metadata: { ip: "192.168.1.55", department: "Administration" },
      created_at: new Date(now.getTime() - 28 * 3600_000).toISOString(),
      admin_user: users[4],
    },
    {
      id: "al-031", admin_user_id: "u1", resource: "deliveries", action: "update",
      resource_id: "LIV-2026-0380", before_data: { driver: "Aliou Cisse" },
      after_data: { driver: "Mamadou Dieng", reason: "Reassignment zone Thies" },
      metadata: { ip: "192.168.1.45", department: "Logistique" },
      created_at: new Date(now.getTime() - 30 * 3600_000).toISOString(),
      admin_user: users[0],
    },
    {
      id: "al-032", admin_user_id: "u2", resource: "products", action: "update",
      resource_id: "PRD-0034", before_data: { price: 2800 },
      after_data: { price: 2950, promo_price: 2500, promo_end: "2026-04-15" },
      metadata: { ip: "192.168.1.32", department: "Produits" },
      created_at: new Date(now.getTime() - 32 * 3600_000).toISOString(),
      admin_user: users[1],
    },
  ];

  return entries;
}
